import {Action, getModule, Module} from 'vuex-module-decorators';
import store from '@/store';
import {httpClient} from '@/api/baseApi';
import {EntityModule} from './entityModule';
import {NodeData} from '@/system/nodes/interfaces';
import {NodeDataType} from '@/system/nodes/enums';
import {
  PModuleMeasurement,
  PModulePosition,
  PModuleWithCoords,
} from '@/views/system/modules/interfaces';
import enumsModule, {transformFromModuleTypeStr} from '../enumsModule';

/*
    Динамический модуль хранилища vuex для работы с деревом узлов
*/

const moduleName = 'nodeModule';

function transformGet(elem: any): NodeData {
  return {
    id: elem.id,
    parentId: elem.parent_id,
    name: elem.name,
    children: elem.children?.map((x: any) => transformGet(x)),
    type: elem.type as NodeDataType,
    coords: elem.x ? {x: elem.x, y: elem.y} : undefined,
    services: elem.services_scheme,
    oil: elem.oil,
    parent: null,
  };
}

//TODO используется ли topValueBoundary и bottomValueBoundary, нужны они здесь и если нет, то удалить, если да, то возможно и здесь будет по 6 границ.
//TODO Вроде эта функция вообще не используется, удалить?
function getMeasurements(elem: any): PModuleMeasurement[] {
  return elem.measurements?.map((elem: any) => ({
    measurementId: elem.module_type_measurement_id,
    measurementDescription: elem.module_type_measurement_description,
    topValueBoundary: elem.top_value_boundary,
    bottomValueBoundary: elem.bottom_value_boundary,
  }));
}

function transformPModuleWithCoordsGet(node_id: number, elem: any): PModuleWithCoords {
  const type = transformFromModuleTypeStr(elem.type);
  const measurements = getMeasurements(elem);
  return {
    id: elem.id,
    guid: elem.guid,
    name: elem.name,
    description: elem.description,
    processStatus: elem.process_status,
    processStatusDescription: elem.process_status_description,
    status: elem.status,
    nodes: elem.nodes,
    type,
    typeDescription: enumsModule.moduleTypes.filter(x => x.value == type)[0].description,
    nodeDescription: elem.node_description || '',
    coords:
      elem.nodes.filter((pos: PModulePosition) => pos.node_id == node_id && pos.x).length > 0
        ? elem.nodes.filter((pos: PModulePosition) => pos.node_id == node_id)[0]
        : undefined,
    measurements,
  };
}

function transformCreateEdit(elem: any): NodeData {
  return {
    name: elem.name,
    parentId: elem.parent_id,
    type: elem.type,
    services: elem.services_scheme,
    oil: elem.oil,
    parent: null,
  };
}

function transformSave(model: NodeData): any {
  return {
    name: model.name,
    parent_id: model.parentId ?? null,
    type: model.type,
    services_scheme: model.services,
  };
}

function transformModulesUpdate(model: {guid: string; x?: number; y?: number}) {
  return {
    guid: model.guid,
    x: model.x ?? null,
    y: model.y ?? null,
  };
}

function transformNodesUpdate(model: {id: number; x?: number; y?: number}) {
  return {
    id: model.id,
    x: model.x ?? null,
    y: model.y ?? null,
  };
}

function transformMove(model: {to: number; inside: boolean}) {
  return {
    id_to: model.to ?? null,
    inside: model.inside,
  };
}

function filterMachineNodes(node: any, type: any): any[] {
  const machineNodes: any[] = [];
  const stack = [node];
  while (stack.length) {
    const x = stack.pop();
    if (x?.type === type) machineNodes.push(x);
    if (x && x.children) x.children.forEach((c: any) => stack.push(c));
  }
  return machineNodes;
}
@Module({
  name: moduleName,
  store: store,
  dynamic: true,
  namespaced: true,
})
export class NodesModule extends EntityModule {
  protected routeName = 'nodes_rel';

  get all(): NodeData[] {
    return super.all as NodeData[];
  }

  get machines(): NodeData[] {
    let machineNodes: NodeData[] = [];
    const nodes = super.all as NodeData[];
    nodes.forEach(x => (machineNodes = machineNodes.concat(filterMachineNodes(x, 4))));
    return machineNodes;
  }

  //Получение списка объектов с сервера
  @Action({rawError: true})
  public async fetchAll() {
    return await super.fetchAll(transformGet);
  }

  //Получение модели для создания объекта
  @Action
  public async create(): Promise<{data: NodeData}> {
    return super.create(transformCreateEdit) as Promise<{data: NodeData}>;
  }

  //Получение модели для редактирования объекта
  @Action
  public async edit(id: any): Promise<{data: NodeData}> {
    return super.edit(id, transformCreateEdit) as Promise<{data: NodeData}>;
  }

  //Создание/реактирование объекта
  @Action
  public async save(model: NodeData) {
    return super.save(model, transformSave);
  }

  //Удаление узла (в базовом классе)

  //Получение списка дочерних узлов
  @Action
  public async getChildNodes(id: number): Promise<NodeData[]> {
    const {data} = await httpClient.get(`${this.routeName}/${id}/nodes/`);
    const transformData = data.map((elem: any) => transformGet(elem));
    return transformData;
  }

  //Перемещение узла
  @Action
  public async move(params: {id: number; to: number; inside: boolean}): Promise<boolean> {
    await httpClient.patch(
      `${this.routeName}/${params.id}`,
      transformMove({to: params.to, inside: params.inside}),
    );
    return true;
  }

  //Загрузка мнемосхемы для узла
  @Action({rawError: true})
  public async uploadScheme(params: {id: number; formData: FormData}) {
    try {
      const {data} = await httpClient.post<{content: string}>(
        `${this.routeName}/${params.id}/schema/`,
        params.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      return data.content;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение мнемосхемы для узла
  @Action
  public async getScheme(id: number) {
    try {
      const {data} = await httpClient.get<string>(`${this.routeName}/${id}/schema/`);
      return data;
    } catch {
      return null;
    }
  }

  //Получение списка ПМ для узла
  @Action
  public async getModules(id: number): Promise<PModuleWithCoords[]> {
    const {data} = await httpClient.get<[]>(`${this.routeName}/${id}/modules/`);
    return data.map(x => transformPModuleWithCoordsGet(id, x));
  }

  //Обновление списка ПМ у узла с координатами
  @Action
  public async batchSetModules(params: {
    id: number;
    modules: {guid: string; x?: number; y?: number}[];
  }) {
    await httpClient.put(
      `${this.routeName}/${params.id}/modules/`,
      params.modules.map((x: any) => transformModulesUpdate(x)),
    );
  }

  //Обновление списка подузлов у узла с координатами
  @Action
  public async batchUpdateNodes(params: {
    id: number;
    nodes: {id: number; x?: number; y?: number}[];
  }) {
    await httpClient.put(
      `${this.routeName}/${params.id}/nodes/`,
      params.nodes.map((x: any) => transformNodesUpdate(x)),
    );
  }
}

export default getModule(NodesModule);
